<template>
  <div class="section">
    <div class="container custom-container">
      <!-- Report -->
      <router-link
        v-if="status==2"
        :to="{name: 'report', params: { id: simulationId}}"
        class="button is-primary is-pulled-right"
      >Create Report</router-link>

      <h1 class="title">
        <Loader v-bind:isLoading="isLoading" />Results
      </h1>

      <p class="my-readable-paragraph has-text-justified">
        Electromagnetics and thermal simulations were performed using ANSYS Workbench&nbsp;2019R3,
        ANSYS Electromagnetic Desktop&nbsp;2019R3 - including ANSYS HFSS and ANSYS Circuit Design
        - and ANSYS Mechanical&nbsp;2019R3 (Transient Thermal component). See FAQs for more details and references.
      </p>

      <div v-if="status==2">
        <!-- Title -->
        <h2 class="title is-4 simulation-title">{{ indata.title }}</h2>

        <!-- Description -->
        <p>
          <span class="is-italic" v-if="indata.description !== ''">{{ indata.description }} &ndash;</span>
          {{ created_at | formatDate }}
        </p>

        <button
          id='btn-toggle-inputs'
          class="button is-light toggle-button"
          v-on:click="toggleInputExpand"
        >{{inputExpandLabel}}</button>

        <div class="tight-layout" v-show="isInputExpanded">
          <div class="columns">
            <div class="column">
              <h3 class="title is-5">Summary</h3>
              <h4 class="title is-6">Device</h4>
              <Definition id="implant" label="Implant" :value="implantName" />
              <Definition v-if="indata.Implant.Name == 'Import'" id="implant-model-file" label="Filename" :value="indata.Import" />
              <Definition id="implant-position" label="Position" :value="implantPosition" />
              <div id="implant-dimensions" v-if="indata.Implant.Name != 'Import'">
                <div v-for="(item, index) in implantDimensions" v-bind:key="index">
                  <Definition :label="item.label" :value="item.msg" />
                </div>
              </div>
            </div>
            <div id="insulation" class="column">
              <h3 class="title is-5 has-text-white">Summary</h3>
              <h4 class="title is-6">Insulation</h4>
              <div v-if="isInsulation">
                <div v-for="(item, index) in implantInsulation" v-bind:key="index">
                  <Definition :label="item.label" :value="item.msg" />
                </div>
              </div>
              <p v-else>n/a</p>
            </div>
            <div id="reference-rod" class="column">
              <h3 class="title is-5 has-text-white">Summary</h3>
              <h4 class="title is-6">Reference Rod</h4>
              <div v-if="indata.RefRod !== null">
                <Definition label="Type" :value="indata.RefRod.Name" />
                <Definition label="Diameter" :value="indata.RefRod.Size.D + ' mm'" />
                <Definition label="Length" :value="indata.RefRod.Size.L + ' mm'" />
                <Definition v-if="indata.RefRod.Name == 'Hollow Rod'" label="Thickness" :value="indata.RefRod.Size.T + ' mm'" />
                <Definition label="Position" :value="referenceRodPosition" />
              </div>
              <p v-else>n/a</p>
            </div>
            <div class="column">
              <h3 class="title is-5 has-text-white">Summary</h3>
              <h4 class="title is-6">Phantom</h4>
              <Definition label="Length" :value="indata.Phantom.Size.L + ' mm'" />
              <Definition label="Height" :value="indata.Phantom.Size.W + ' mm'" />
              <Definition label="Width" :value="indata.Phantom.Size.T + ' mm'" />
            </div>
          </div>

          <div class="columns">
            <div id="implant-material-properties" class="column">
              <h3 class="title is-5">Materials</h3>
              <h4 class="title is-6">Device</h4>
              <Definition label="Material" :value="indata.Implant.Material.Name" />
              <Definition label="Density" id="implant-density" :value="indata.Implant.Material.Density + ' kg/m³'" />
              <Definition label="Conductivity" id="implant-conductivity" :value="indata.Implant.Material.Cond + ' S/m'" />
              <Definition label="Rel. Permittivity" id="implant-permittivity" :value="indata.Implant.Material.Perm" />
              <Definition label="Thermal Cond." id="implant-thermal-conductivity" :value="indata.Implant.Material.Th_Cond + ' W/(m·°C)'" />
              <Definition label="Heat Capacity" id="implant-heat-capacity" :value="indata.Implant.Material.Sp_Heat + ' J/(kg·°C)'" />
            </div>
            <div class="column">
              <h3 class="title is-5 has-text-white">Materials</h3>
              <h4 class="title is-6">Insulation</h4>
              <div v-if="isInsulation">
                <Definition label="Material" id="insulation-material-name" :value="this.indata.Implant.Insulation.Name" />
                <Definition label="Density" :value="indata.Implant.Insulation.Density + ' kg/m³'" />
                <Definition label="Conductivity" :value="indata.Implant.Insulation.Cond + ' S/m'" />
                <Definition label="Rel. Permittivity" :value="indata.Implant.Insulation.Perm" />
                <Definition label="Thermal Cond." :value="indata.Implant.Insulation.Th_Cond + ' W/(m·°C)'" />
                <Definition label="Heat Capacity" :value="indata.Implant.Insulation.Sp_Heat + ' J/(kg·°C)'" />
              </div>
              <p v-else>n/a</p>
            </div>
            <div id="reference-rod-material" class="column">
              <h3 class="title is-5 has-text-white">Materials</h3>
              <h4 class="title is-6">Reference Rod</h4>
              <div v-if="indata.RefRod !== null">
                <Definition label="Material" :value="this.indata.RefRod.Material.Name" />
                <Definition label="Density" :value="indata.RefRod.Material.Density + ' kg/m³'" />
                <Definition label="Conductivity" :value="indata.RefRod.Material.Cond + ' S/m'" />
                <Definition label="Rel. Permittivity" :value="indata.RefRod.Material.Perm" />
                <Definition label="Thermal Cond." :value="indata.RefRod.Material.Th_Cond + ' W/(m·°C)'" />
                <Definition label="Heat Capacity" :value="indata.RefRod.Material.Sp_Heat + ' J/(kg·°C)'" />
              </div>
              <p v-else>n/a</p>
            </div>
            <div class="column">
              <h3 class="title is-5 has-text-white">Materials</h3>
              <h4 class="title is-6">Phantom</h4>
              <Definition label="Material" value="Gelled Saline" />
              <Definition label="Density" :value="indata.Phantom.Material.Density + ' kg/m³'" />
              <Definition label="Conductivity" :value="indata.Phantom.Material.Cond + ' S/m'" />
              <Definition label="Rel. Permittivity" :value="indata.Phantom.Material.Perm" />
              <Definition label="Thermal Cond." :value="indata.Phantom.Material.Th_Cond + ' W/(m·°C)'" />
              <Definition label="Heat Capacity" :value="indata.Phantom.Material.Sp_Heat + ' J/(kg·°C)'" />
            </div>
          </div>

          <div class="columns">
            <div id="coil-properties" class="column is-3">
              <h3 class="title is-5">Setup</h3>
              <h4 class="title is-6">RF Coil</h4>
              <Definition label="Diameter" :value="coil[indata.SimSetup.Frequency].D + ' mm'" />
              <Definition label="Length" :value="coil[indata.SimSetup.Frequency].L + ' mm'" />
              <Definition label="Frequency" :value="indata.SimSetup.Frequency + ' MHz'" />
              <Definition label="Power" :value="indata.SimSetup.Power + ' W / kg'" />
              <Definition label="Polarization" :value="polarizations[indata.SimSetup.Polarization]" />
            </div>
            <div id="thermal" class="column is-3">
              <h3 class="title is-5 has-text-white">Setup</h3>
              <h4 class="title is-6">Thermal Simulation</h4>
              <Definition label="Duration" :value="indata.SimSetup.TDuration + ' s'" />
            </div>
          </div>
        </div><!-- end of div with inputs -->

        <div class="tabs is-centered is-toggle tabsWithSpace">
          <ul>
            <li v-bind:class="{ 'is-active': isActive(3) }">
              <a v-on:click="selectTab(3)">Max and Probes</a>
            </li>
            <li v-bind:class="{ 'is-active': isActive(0) }">
              <a v-on:click="selectTab(0)">E Field Background</a>
            </li>
            <li v-bind:class="{ 'is-active': isActive(4) }">
              <a v-on:click="selectTab(4)">H Field Background</a>
            </li>
            <li v-bind:class="{ 'is-active': isActive(1) }">
              <a v-on:click="selectTab(1)">Raw Local SAR</a>
            </li>
            <li v-bind:class="{ 'is-active': isActive(2) }">
              <a v-on:click="selectTab(2)">AvgSAR 1g</a>
            </li>
            <li v-bind:class="{ 'is-active': isActive(5) }">
              <a v-on:click="selectTab(5)">Temperature</a>
            </li>
            <li v-bind:class="{ 'is-active': isActive(6) }">
              <a id="simulation-info-link" v-on:click="selectTab(6)">Simulation Info</a>
            </li>
          </ul>
        </div>
        <div v-show="activeTab == 3">
          <!-- Max and Probes -->
          <h2 class="title is-4 breatheTop">Absolute Maxima</h2>
          <table class="table">
            <colgroup>
              <col span="1" />
              <col span="3" class="has-background-light" />
              <col span="2" />
            </colgroup>
            <thead>
              <tr>
                <th rowspan="2">Quantity</th>
                <th colspan="3" style="border-style: none" class="has-text-centered">Location</th>
                <th rowspan="2">
                  Background Value
                  <font-awesome-icon
                    icon="info-circle"
                    title="Computed value without the implant in place."
                    style="cursor: help;"
                  />
                </th>
                <th rowspan="2">Maximum</th>
              </tr>
              <tr>
                <th>x (mm)</th>
                <th>y (mm)</th>
                <th>z (mm)</th>
              </tr>
            </thead>
            <tbody>
              <tr id="local-sar">
                <td>Local Raw SAR (W/kg)</td>
                <td class="has-text-right">{{outdata.Maxima["Local SAR"].X | normalPrecision}}</td>
                <td class="has-text-right">{{outdata.Maxima["Local SAR"].Y | normalPrecision}}</td>
                <td class="has-text-right">{{outdata.Maxima["Local SAR"].Z | normalPrecision}}</td>
                <td class="has-text-right">{{outdata.Maxima["Local SAR"]["Background Value"] | normalPrecision}}</td>
                <td class="has-text-right">{{outdata.Maxima["Local SAR"].Value | normalPrecision}}</td>
              </tr>
              <tr id="avg-sar">
                <td>AvgSAR 1g (W/kg)</td>
                <td class="has-text-right">{{outdata.Maxima["Average SAR"].X | normalPrecision}}</td>
                <td class="has-text-right">{{outdata.Maxima["Average SAR"].Y | normalPrecision}}</td>
                <td class="has-text-right">{{outdata.Maxima["Average SAR"].Z | normalPrecision}}</td>
                <td class="has-text-right">{{outdata.Maxima["Average SAR"]["Background Value"] | normalPrecision }}</td>
                <td class="has-text-right">{{outdata.Maxima["Average SAR"].Value | normalPrecision}}</td>
              </tr>
              <tr id="temperature">
                <td>Temperature (°C)</td>
                <td class="has-text-right">{{outdata.Maxima["Temperature"].X | normalPrecision}}</td>
                <td class="has-text-right">{{outdata.Maxima["Temperature"].Y | normalPrecision}}</td>
                <td class="has-text-right">{{outdata.Maxima["Temperature"].Z | normalPrecision}}</td>
                <td class="has-text-right">{{outdata.Maxima["Temperature"]["Background Value"] | normalPrecision }}</td>
                <td class="has-text-right">{{outdata.Maxima["Temperature"].Value | normalPrecision}}</td>
              </tr>
            </tbody>
          </table>
          <h2 class="title is-4 breatheTop" v-if="indata.Probes != null">Probes</h2>
          <div v-for="(p, index) in outdata.Probes" v-bind:key="p.Name">
            <h2 class="title is-5 probeTitle">Probe {{index + 1}}</h2>
            <table class="table">
              <colgroup>
                <col span="1" />
                <col span="3" class="has-background-light" />
                <col span="2" />
              </colgroup>
              <thead>
                <tr>
                  <th rowspan="2">Quantity</th>
                  <th colspan="3" style="border-style: none" class="has-text-centered">Location</th>
                  <th rowspan="2">Background Value</th>
                  <th rowspan="2">Maximum</th>
                </tr>
                <tr>
                  <th>x (mm)</th>
                  <th>y (mm)</th>
                  <th>z (mm)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-bind:id='"probe-" + (index + 1) + "-avg-sar"'>
                  <td>AvgSAR 1g (W/kg)</td>
                  <td class="has-text-right">{{p["Average SAR"].X | normalPrecision}}</td>
                  <td class="has-text-right">{{p["Average SAR"].Y | normalPrecision}}</td>
                  <td class="has-text-right">{{p["Average SAR"].Z | normalPrecision}}</td>
                  <td class="has-text-right">{{p["Average SAR"]["Background Value"] | normalPrecision}}</td>
                  <td class="has-text-right">{{p["Average SAR"].Value | normalPrecision}}</td>
                </tr>
                <tr v-bind:id='"probe-" + (index + 1) + "-temperature"'>
                  <td>Temperature (°C)</td>
                  <td class="has-text-right">{{p.Temperature.X | normalPrecision}}</td>
                  <td class="has-text-right">{{p.Temperature.Y | normalPrecision}}</td>
                  <td class="has-text-right">{{p.Temperature.Z | normalPrecision}}</td>
                  <td class="has-text-right">{{p.Temperature["Background Value"] | normalPrecision}}</td>
                  <td class="has-text-right">{{p.Temperature.Value | normalPrecision}}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <!-- Maxima and Probes -->
          <br />
          <h2 class="title is-4 breatheTop">Location of Maxima and Probes</h2>
          <p>Legend</p>
          <ul>
            <!--<li><span style="color: gray">gray</span> = implant volume</li> True or not? -->
            <li>black = probes</li>
            <li><span style="color: green">green</span> = Max1gSAR</li>
            <li><span style="color: red">red</span> = maximum temperature</li>
          </ul>
          <h2 class="title is-5 moreBreatheTop">Probes and Maxima Location in the Phantom</h2>
          <div class="box my-card">
            <img id="location-phantom-y" :src="imageList['Fig_Location'].Phantom[1]" />
          </div>
          <div class="box my-card">
            <img id="location-phantom-z" :src="imageList['Fig_Location'].Phantom[2]" />
          </div>
          <div class="box my-card">
            <img id="location-phantom-x" :src="imageList['Fig_Location'].Phantom[0]" />
          </div>
          <h2 class="title is-5 breatheTop">Probes and Maxima Location in the Implant Volume (Gray)</h2>
          <div class="box my-card">
            <img id="location-implant-y" :src="imageList['Fig_Location'].ImpVolume[1]" />
          </div>
          <div class="box my-card">
            <img id="location-implant-z" :src="imageList['Fig_Location'].ImpVolume[2]" />
          </div>
          <div class="box my-card">
            <img id="location-implant-x" :src="imageList['Fig_Location'].ImpVolume[0]" />
          </div>
        </div>
        <div v-show="activeTab == 0">
          <!-- E Field Background -->
          <h2 class="title is-5 breatheTop">Phantom</h2>
          <ImageStorage id="phantom-mag-e-y" v-bind:images="sortedImages(imageList.Figure.Coil.Phantom.Mag_E.Y)" />
          <ImageStorage id="phantom-mag-e-x" v-bind:images="sortedImages(imageList.Figure.Coil.Phantom.Mag_E.X)" />
          <ImageStorage id="phantom-mag-e-z" v-bind:images="sortedImages(imageList.Figure.Coil.Phantom.Mag_E.Z)" />

          <h2 class="title is-5 breatheTop">Implant Volume</h2>
          <ImageStorage id="implant-mag-e-y" v-bind:images="sortedImages(imageList.Figure.Coil.ImpVolume.Mag_E.Y)" />
          <ImageStorage id="implant-mag-e-x" v-bind:images="sortedImages(imageList.Figure.Coil.ImpVolume.Mag_E.X)" />
          <ImageStorage id="implant-mag-e-z" v-bind:images="sortedImages(imageList.Figure.Coil.ImpVolume.Mag_E.Z)" />
        </div>
        <div v-show="activeTab == 4">
          <!-- H Field Background -->
          <h2 class="title is-5 breatheTop">Phantom</h2>
          <ImageStorage id="phantom-mag-h-y" v-bind:images="sortedImages(imageList.Figure.Coil.Phantom.Mag_H.Y)" />
          <ImageStorage id="phantom-mag-h-x" v-bind:images="sortedImages(imageList.Figure.Coil.Phantom.Mag_H.X)" />
          <ImageStorage id="phantom-mag-h-z" v-bind:images="sortedImages(imageList.Figure.Coil.Phantom.Mag_H.Z)" />

          <h2 class="title is-5 breatheTop">Implant Volume</h2>
          <ImageStorage id="implant-mag-h-y" v-bind:images="sortedImages(imageList.Figure.Coil.ImpVolume.Mag_H.Y)" />
          <ImageStorage id="implant-mag-h-x" v-bind:images="sortedImages(imageList.Figure.Coil.ImpVolume.Mag_H.X)" />
          <ImageStorage id="implant-mag-h-z" v-bind:images="sortedImages(imageList.Figure.Coil.ImpVolume.Mag_H.Z)" />
        </div>
        <div v-show="activeTab == 1">
          <!-- Local SAR -->
          <h2 class="title is-5 breatheTop">Phantom</h2>
          <ImageStorage id="phantom-local-sar-y" v-bind:images="sortedImages(imageList.Figure.Load.Phantom.Local_SAR.Y, true)" />
          <ImageStorage id="phantom-local-sar-x" v-bind:images="sortedImages(imageList.Figure.Load.Phantom.Local_SAR.X, true)" />
          <ImageStorage id="phantom-local-sar-z" v-bind:images="sortedImages(imageList.Figure.Load.Phantom.Local_SAR.Z, true)" />
          <h2 class="title is-5 breatheTop">Implant Volume</h2>
          <ImageStorage id="implant-local-sar-y" v-bind:images="sortedImages(imageList.Figure.Load.ImpVolume.Local_SAR.Y)" />
          <ImageStorage id="implant-local-sar-x" v-bind:images="sortedImages(imageList.Figure.Load.ImpVolume.Local_SAR.X)" />
          <ImageStorage id="implant-local-sar-z" v-bind:images="sortedImages(imageList.Figure.Load.ImpVolume.Local_SAR.Z)" />
        </div>
        <div v-show="activeTab == 2">
          <!-- Average SAR -->
          <h2 class="title is-5 breatheTop">Phantom</h2>
          <ImageStorage id="phantom-avg-sar-y" v-bind:images="sortedImages(imageList.Figure.Load.Phantom.Average_SAR.Y, true)" />
          <ImageStorage id="phantom-avg-sar-x" v-bind:images="sortedImages(imageList.Figure.Load.Phantom.Average_SAR.X, true)" />
          <ImageStorage id="phantom-avg-sar-z" v-bind:images="sortedImages(imageList.Figure.Load.Phantom.Average_SAR.Z, true)" />
          <h2 class="title is-5 breatheTop">Implant Volume</h2>
          <ImageStorage
            id="implant-avg-sar-y"
            v-bind:images="sortedImages(imageList.Figure.Load.ImpVolume.Average_SAR.Y)"
          />
          <ImageStorage
            id="implant-avg-sar-x"
            v-bind:images="sortedImages(imageList.Figure.Load.ImpVolume.Average_SAR.X)"
          />
          <ImageStorage
            id="implant-avg-sar-z"
            v-bind:images="sortedImages(imageList.Figure.Load.ImpVolume.Average_SAR.Z)"
          />
        </div>
        <div v-show="activeTab == 5">
          <!-- Temperature -->
          <h2 class="title is-5 breatheTop">Phantom</h2>
          <ImageStorage id="phantom-temperature-y" v-bind:images="sortedImages(imageList.Figure.Load.Phantom.Temperature.Y)" />
          <ImageStorage id="phantom-temperature-x" v-bind:images="sortedImages(imageList.Figure.Load.Phantom.Temperature.X)" />
          <ImageStorage id="phantom-temperature-z" v-bind:images="sortedImages(imageList.Figure.Load.Phantom.Temperature.Z)" />
          <h2 class="title is-5 breatheTop">Implant Volume</h2>
          <ImageStorage
            id="implant-temperature-y"
            v-bind:images="sortedImages(imageList.Figure.Load.ImpVolume.Temperature.Y)"
          />
          <ImageStorage
            id="implant-temperature-x"
            v-bind:images="sortedImages(imageList.Figure.Load.ImpVolume.Temperature.X)"
          />
          <ImageStorage
            id="implant-temperature-z"
            v-bind:images="sortedImages(imageList.Figure.Load.ImpVolume.Temperature.Z)"
          />
        </div>
        <div v-show="activeTab == 6">
          <!-- Simulation Info -->
          <figure class="image">
            <img class="simulationSteps" src="../assets/Figure4.png" />
          </figure>
          <h2 class="title is-5 breatheTop">Step 2 - Electromagnetic Simulation</h2>
          <table class="table">
            <thead>
              <tr>
                <th>Geometry</th>
                <th>Number of Tetrahedra</th>
                <th>Min. Edge Length (mm)</th>
                <th>Max. Edge Length (mm)</th>
                <th>RMS Edge Length (mm)</th>
              </tr>
            </thead>
            <tbody>
              <tr id="coil-field-source">
                <td>Field Source</td>
                <td class="has-text-right">{{outdata.Mesh.Coil["Field Source"].Num}}</td>
                <td class="has-text-right">{{outdata.Mesh.Coil["Field Source"].Min | normalPrecision}}</td>
                <td class="has-text-right">{{outdata.Mesh.Coil["Field Source"].Max | normalPrecision}}</td>
                <td class="has-text-right">{{outdata.Mesh.Coil["Field Source"].RMS | normalPrecision}}</td>
              </tr>
              <tr id="coil-phantom">
                <td>ASTM Phantom</td>
                <td class="has-text-right">{{outdata.Mesh.Coil["ASTM Phantom"].Num}}</td>
                <td class="has-text-right">{{outdata.Mesh.Coil["ASTM Phantom"].Min | normalPrecision}}</td>
                <td class="has-text-right">{{outdata.Mesh.Coil["ASTM Phantom"].Max | normalPrecision}}</td>
                <td class="has-text-right">{{outdata.Mesh.Coil["ASTM Phantom"].RMS | normalPrecision}}</td>
              </tr>
            </tbody>
          </table>
          <p>Total number of tetrahedral elements: {{outdata.Mesh.Coil.Tot_tet}}.</p>
          <h2 class="title is-5 moreBreatheTop">Step 3 - Electromagnetic Simulation</h2>
          <table class="table">
            <thead>
              <tr>
                <th>Geometry</th>
                <th>Number of Tetrahedra</th>
                <th>Min. Edge Length (mm)</th>
                <th>Max. Edge Length (mm)</th>
                <th>RMS Edge Length (mm)</th>
              </tr>
            </thead>
            <tbody>
              <tr id="load-phantom">
                <td>ASTM Phantom</td>
                <td class="has-text-right">{{outdata.Mesh.Load["ASTM Phantom"].Num}}</td>
                <td class="has-text-right">{{outdata.Mesh.Load["ASTM Phantom"].Min | normalPrecision}}</td>
                <td class="has-text-right">{{outdata.Mesh.Load["ASTM Phantom"].Max | normalPrecision}}</td>
                <td class="has-text-right">{{outdata.Mesh.Load["ASTM Phantom"].RMS | normalPrecision}}</td>
              </tr>
              <tr id="load-implant-volume">
                <td>Implant Volume</td>
                <td class="has-text-right">{{outdata.Mesh.Load["Implant Volume"].Num}}</td>
                <td class="has-text-right">{{outdata.Mesh.Load["Implant Volume"].Min | normalPrecision}}</td>
                <td class="has-text-right">{{outdata.Mesh.Load["Implant Volume"].Max | normalPrecision}}</td>
                <td class="has-text-right">{{outdata.Mesh.Load["Implant Volume"].RMS | normalPrecision}}</td>
              </tr>
              <tr id="load-implant">
                <td>Implant</td>
                <td class="has-text-right">{{outdata.Mesh.Load["Implant"].Num}}</td>
                <td class="has-text-right">{{outdata.Mesh.Load["Implant"].Min | normalPrecision}}</td>
                <td class="has-text-right">{{outdata.Mesh.Load["Implant"].Max | normalPrecision}}</td>
                <td class="has-text-right">{{outdata.Mesh.Load["Implant"].RMS | normalPrecision}}</td>
              </tr>
            </tbody>
          </table>
          <p>Total number of tetrahedral elements: {{outdata.Mesh.Load.Tot_tet}}.</p>
        </div>
      </div>
      <div v-else-if="status===4">
        <p class="notification is-danger moreBreatheTop">
          Simulation failed.
          <span v-if="errorMsg != ''">
            An error message is available: &#96;{{errorMsg}}&#96;.
          </span>
          <span v-else>
            No error message available to display.
          </span>
        </p>
      </div>
    </div>
  </div>
</template>

<style scoped>
img.simulationSteps {
  height: 26em;
  width: auto;
  margin-bottom: 1.5em;
}
.tabsWithSpace {
  margin-top: 2em;
  margin-bottom: 2em;
}
p.my-readable-paragraph {
  max-width: 42em;
}
.tight-layout .columns {
  margin-bottom: 1em;
}
.tight-layout h3 {
  margin-bottom: 0.5em;
}
.tight-layout h4 {
  margin-bottom: 0.5em;
}
.simulation-title {
  margin-top: 1.5em;
  margin-bottom: 0.5em;
}

hr {
  margin-top: 0px;
  margin-bottom: 0.5em;
}
.toggle-button {
  margin-top: 1em;
  margin-bottom: 1.5em;
}
.my-card {
  display: inline-block;
  margin-right: 1em;
  margin-bottom: 1em;
  max-width: none;
}
.my-pagination {
  padding-top: 0.5em;
}
.my-image {
  display: block;
  margin: auto;
}
.mesh-results-title {
  margin-top: 0.5em;
}
.solo-definitions {
  margin-bottom: 1em;
}
h2.breatheTop {
  margin-top: 0.5em;
}
h2.moreBreatheTop, p.moreBreatheTop {
  margin-top: 1em;
}
h2.probeTitle {
  margin-top: 2em;
}
ul {
  list-style: disc inside;
  padding-bottom: 0.5em;
  padding-top: 0.5em;
}
ul > li {
  margin-left: 1em;
  padding-bottom: 0.25em;
}
</style>

<script>
import Definition from '@/components/Definition.vue';
import ImageStorage from '@/components/ImageStorage.vue';
import Loader from '@/components/Loader.vue';
import apiCall from '../lib/remote-calls';

export default {
  components: {
    Definition,
    ImageStorage,
    Loader,
  },
  data() {
    return {
      created_at: '',
      simulationId: null,
      indata: {
        Implant: {
          Name: '',
          Material: {
            Name: '',
          },
          Position: {
            X: 0,
            Y: 0,
            Z: 0,
          },
          Insulation: null,
        },
        SimSetup: {
          Frequency: 0,
          TDuration: '',
        },
        Phantom: {
          Size: {
            L: 0,
            T: 0,
            W: 0,
          },
          Material: {
            Name: '',
          },
        },
        RefRod: null,
      },
      outdata: {
        Mesh: {
          Coil: {
            'Field Source': {},
            'ASTM Phantom': {},
          },
          Load: {
            Implant: {},
            'ASTM Phantom': {},
            'Implant Volume': {},
          },
        },
        Max_Temperature: 0,
        Maxima: {
          'Local SAR': {
            X: 0,
            Y: 0,
            Z: 0,
            Value: 0,
            'Background Value': 0,
          },
          'Average SAR': {
            X: 0,
            Y: 0,
            Z: 0,
            Value: 0,
            'Background Value': 0,
          },
          Temperature: {
            X: 0,
            Y: 0,
            Z: 0,
            Value: 0,
            'Background Value': 0,
          },
        },
        Final_ImplantPos: {
          X: 0,
          Y: 0,
          Z: 0,
        },
      },
      status: 0,
      coil: {
        0: { D: 0, L: 0 },
        64: { D: 420, L: 650 },
        128: { D: 610, L: 620 },
      },
      polarizations: {
        CP1: 'Circular 1',
        CP2: 'Circular 2',
        LP1: 'Linear 1',
        LP2: 'Linear 2',
      },
      isLoading: false,
      isInputExpanded: false,
      activeTab: 3,
      imageList: {
        Fig_Location: { Phantom: [], ImpVolume: [] },
        Figure: {
          Coil: {
            Phantom: {
              Mag_E: { X: [], Y: [], Z: [] },
              Mag_H: { X: [], Y: [], Z: [] },
            },
            ImpVolume: {
              Mag_E: { X: [], Y: [], Z: [] },
              Mag_H: { X: [], Y: [], Z: [] },
            },
          },
          Load: {
            Phantom: {
              Local_SAR: { X: [], Y: [], Z: [] },
              Average_SAR: { X: [], Y: [], Z: [] },
              Temperature: { X: [], Y: [], Z: [] },
            },
            ImpVolume: {
              Local_SAR: { X: [], Y: [], Z: [] },
              Average_SAR: { X: [], Y: [], Z: [] },
              Temperature: { X: [], Y: [], Z: [] },
            },
          },
        },
      },
      errorMsg: '',
    };
  },
  methods: {
    sortedImages(blobs, removeFirstImage = false) {
      const sorted = Array.from(blobs).sort(
        (a, b) => parseInt(
          a
            .split('/')
            .slice(-1)[0]
            .split('_')[0],
          10,
        )
          - parseInt(
            b
              .split('/')
              .slice(-1)[0]
              .split('_')[0],
            10,
          ),
      );
      if (removeFirstImage === true) {
        sorted.shift();
      }
      return sorted;
    },
    async requestData() {
      try {
        const response = await apiCall(
          'GET',
          `simulations/${this.simulationId}`,
        );
        this.created_at = response.data.created_at;
        this.indata = response.data.indata;
        // console.debug('inputs', this.indata);
        this.outdata = response.data.outdata;
        // console.debug('outputs', this.outdata);
        if (this.outdata !== null && 'msg' in this.outdata) {
          this.errorMsg = this.outdata.msg;
        }
        this.status = response.data.status;
        this.isLoading = false;
      } catch (error) {
        console.error(error);
      }
    },
    async requestImageList() {
      try {
        const response = await apiCall('GET', `media/${this.simulationId}`);
        this.imageList = response.data;
      } catch (error) {
        console.error(error);
      }
    },
    toggleInputExpand() {
      this.isInputExpanded = !this.isInputExpanded;
    },
    isActive(tab) {
      if (tab === this.activeTab) {
        return true;
      }
      return false;
    },
    selectTab(tab) {
      this.activeTab = tab;
    },
  },
  computed: {
    implantName() {
      if (this.indata.Implant.Name === 'Import') {
        return 'Geometry';
      }
      return this.indata.Implant.Name;
    },
    implantPosition() {
      if ('Final_ImplantPos' in this.outdata) {
        const x = this.outdata.Final_ImplantPos.X;
        const y = this.outdata.Final_ImplantPos.Y;
        const z = this.outdata.Final_ImplantPos.Z;
        return `X=${x}, Y=${y}, Z=${z}`;
      }
      const x = this.indata.Implant.Position.X;
      const y = this.indata.Implant.Position.Y;
      const z = this.indata.Implant.Position.Z;
      return `X=${x}, Y=${y}, Z=${z}`;
    },
    implantDimensions() {
      if (this.indata.Implant.Name === 'Stent') {
        const stentOne = this.indata.Stent.Stent1;
        const stentOneDimensions = `D=${stentOne.D}, L=${stentOne.L}, T=${stentOne.T} mm`;
        if (this.indata.Stent.Number === 1) {
          return [{ label: 'Stent 1', msg: stentOneDimensions }];
        }
        if (this.indata.Stent.Number === 2) {
          const stentTwo = this.indata.Stent.Stent2;
          const stentTwoDimensions = `D=${stentTwo.D}, L=${stentTwo.L}, T=${stentTwo.T} mm`;
          return [
            { label: 'Stent 1', msg: stentOneDimensions },
            { label: 'Stent 2', msg: stentTwoDimensions },
            { label: 'Overlap', msg: `${this.indata.Stent.Overlap} mm` },
          ];
        }
        return null;
      }
      if (this.indata.Implant.Name === 'Rod') {
        const rod = this.indata.Rod;
        const rodDimensions = [
          { label: 'Rod', msg: `D=${rod.D}, L=${rod.L} mm` },
        ];
        if (rod.TipNumber > 0) {
          const tipInfo = {
            label: `Tips (${rod.TipNumber})`,
            msg: `${rod.Tip1_L} mm`,
          };
          if (rod.TipNumber === 2) {
            tipInfo.msg += `, ${rod.Tip2_L} mm`;
          }
          rodDimensions.push(tipInfo);
        }
        return rodDimensions;
      }
      return null;
    },
    isInsulation() {
      return (
        (this.indata.Implant.Name === 'Stent'
          && (this.indata.Stent.Insulation1_T !== null
            || this.indata.Stent.Insulation2_T !== null))
        || (this.indata.Implant.Name === 'Rod'
          && this.indata.Rod.Insulation_T !== null)
      );
    },
    implantInsulation() {
      const insulation = [];
      if (this.indata.Implant.Name === 'Stent') {
        if (this.indata.Stent.Insulation1_T !== null) {
          insulation.push({
            label: 'Thickness (1)',
            msg: `${this.indata.Stent.Insulation1_T} mm`,
          });
        }
        if (
          this.indata.Stent.Number === 2
          && this.indata.Stent.Insulation2_T !== null
        ) {
          insulation.push({
            label: 'Thickness (2)',
            msg: `${this.indata.Stent.Insulation2_T} mm`,
          });
        }
      }
      if (this.indata.Implant.Name === 'Rod') {
        insulation.push({
          label: 'Thickness',
          msg: this.indata.Rod.Insulation_T,
        });
      }
      return insulation;
    },
    description() {
      return this.desc || 'No description.';
    },
    inputExpandLabel() {
      if (this.isInputExpanded === true) {
        return 'Hide Inputs';
      }
      return 'Show Inputs';
    },
    referenceRodPosition() {
      return `X=${this.indata.RefRod.Position.X}; Y=${this.indata.RefRod.Position.Y}; Z=${this.indata.RefRod.Position.Z}`;
    },
  },
  mounted() {
    this.isLoading = true;
    this.simulationId = this.$route.params.id;
    this.requestData();
    this.requestImageList();
  },
};
</script>
